/**
 * Notifiche push sul telefono (Andrea 2026-10-16).
 *
 * Come funziona: il browser crea una "subscription" (un indirizzo segreto
 * del telefono) che mandiamo al backend (POST /push/subscribe); il backend
 * la usa per mandare le push (pywebpush + VAPID). Il service worker
 * (src/sw.ts) mostra la notifica e al tocco apre la pagina.
 *
 * Limiti da dire agli utenti:
 * - iPhone/iPad: solo con l'app aggiunta alla schermata Home DA SAFARI
 *   (iOS 16.4+). Nel browser normale non si puo'.
 * - permesso negato una volta: si riattiva dalle impostazioni del telefono
 *   (il browser non lo richiede piu').
 */

import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { inStandalone, piattaforma } from "./installazione";

export type Supporto = "ok" | "ios_browser" | "no";
export type Permesso = NotificationPermission | "n/d";

/** Le categorie scelte dall'utente (users.preferenze_notifiche.push). */
export interface PreferenzePush {
  mercato: boolean;
  giornata: boolean;
  formazione: boolean;
  stampa: boolean;
}
export const PUSH_DEFAULT: PreferenzePush = {
  mercato: true,
  giornata: true,
  formazione: false,
  stampa: false,
};

export function preferenzePush(prefs: Record<string, unknown> | undefined | null): PreferenzePush {
  const p = (prefs?.push ?? {}) as Partial<Record<keyof PreferenzePush, unknown>>;
  return {
    mercato: typeof p.mercato === "boolean" ? p.mercato : PUSH_DEFAULT.mercato,
    giornata: typeof p.giornata === "boolean" ? p.giornata : PUSH_DEFAULT.giornata,
    formazione: typeof p.formazione === "boolean" ? p.formazione : PUSH_DEFAULT.formazione,
    stampa: typeof p.stampa === "boolean" ? p.stampa : PUSH_DEFAULT.stampa,
  };
}

/** Questo telefono/browser puo' ricevere push? */
export function supportoPush(): Supporto {
  if (typeof window === "undefined") return "no";
  const ha = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  if (ha) return "ok";
  // su iOS le API compaiono solo dentro l'app installata da Safari
  if (piattaforma() === "ios" && !inStandalone()) return "ios_browser";
  return "no";
}

export function permessoAttuale(): Permesso {
  if (typeof Notification === "undefined") return "n/d";
  return Notification.permission;
}

// chiave VAPID: base64url → bytes per pushManager.subscribe
function base64UrlToUint8Array(s: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function registrazioneSw(): Promise<ServiceWorkerRegistration> {
  // in dev il SW e' spento (vite.config devOptions): ready resterebbe appeso
  const pronta = navigator.serviceWorker.ready;
  const timeout = new Promise<never>((_, rej) =>
    setTimeout(() => rej(new Error("Service worker non attivo")), 8000),
  );
  return Promise.race([pronta, timeout]);
}

/** La subscription di questo dispositivo, se c'e'. */
export async function subscriptionAttuale(): Promise<PushSubscription | null> {
  if (supportoPush() !== "ok") return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    return (await reg?.pushManager.getSubscription()) ?? null;
  } catch {
    return null;
  }
}

export class PermessoNegatoError extends Error {}

/** Chiede il permesso, crea la subscription e la registra sul backend. */
export async function attivaPush(): Promise<void> {
  const permesso = await Notification.requestPermission();
  if (permesso !== "granted") throw new PermessoNegatoError("Permesso negato");
  const reg = await registrazioneSw();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { data } = await api.get<{ public_key: string }>("/push/vapid-public-key");
    if (!data.public_key) throw new Error("Chiave push non configurata sul server");
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(data.public_key),
    });
  }
  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: sub.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
  });
}

/** Toglie la subscription di questo dispositivo (qui e sul backend). */
export async function disattivaPush(): Promise<void> {
  const sub = await subscriptionAttuale();
  if (!sub) return;
  try {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
  } finally {
    await sub.unsubscribe();
  }
}

/** Stato push di questo dispositivo per la pagina Profilo. */
export function usePushDispositivo() {
  const supporto = supportoPush();
  const [attive, setAttive] = useState<boolean | null>(supporto === "ok" ? null : false);
  const [permesso, setPermesso] = useState<Permesso>(permessoAttuale());

  const aggiorna = useCallback(async () => {
    const sub = await subscriptionAttuale();
    setAttive(sub !== null);
    setPermesso(permessoAttuale());
  }, []);

  useEffect(() => {
    void aggiorna();
  }, [aggiorna]);

  return { supporto, attive, permesso, aggiorna };
}
